<script setup lang="ts">
import {
  addressCount,
  bracketLayout,
  type Cidr,
  type CloudMode,
  formatCidr,
  formatIpv4,
  isAllowedSize,
  lastAddress,
  maskFromPrefix,
  networkAddress,
  usableHostCount,
  usableRange,
} from '@it-tools/core-net'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getNote, ROW_COLORS } from '../model/design'
import type { Design, SubnetNote } from '../model/design'

const props = defineProps<{ design: Design; cloudMode: CloudMode }>()
const emit = defineEmits<{
  split: [Cidr]
  join: [Cidr]
  annotate: [Cidr, Partial<SubnetNote>]
}>()

const { t, n } = useI18n()

interface Row {
  cidr: Cidr
  key: string
  network: string
  mask: string
  range: string
  usable: number
  note: SubnetNote
  canSplit: boolean
  tooSmall: boolean
}

const rows = computed<Row[]>(() =>
  props.design.tree.leaves.map((cidr) => {
    const range = usableRange(cidr, props.cloudMode)
    return {
      cidr,
      key: formatCidr(cidr),
      network: formatCidr(cidr),
      mask: formatIpv4(maskFromPrefix(cidr.prefix)),
      range: range
        ? `${formatIpv4(range.first)} – ${formatIpv4(range.last)}`
        : t('table.noUsableRange'),
      usable: usableHostCount(cidr, props.cloudMode),
      note: getNote(props.design, cidr),
      canSplit: cidr.prefix < 32,
      tooSmall: !isAllowedSize(cidr, props.cloudMode),
    }
  }),
)

/**
 * Nested split/join bracket, mirroring visualsubnetcalc.com: one column per level,
 * each bar drawn once on its block's first row and stretched down with `rowspan`.
 * Geometry comes from core-net so it stays unit-tested.
 */
const bracket = computed(() => bracketLayout(props.design.tree))

function rangeOf(cidr: Cidr): string {
  return `${formatIpv4(networkAddress(cidr))} – ${formatIpv4(lastAddress(cidr))}`
}

function totalOf(cidr: Cidr): number {
  return addressCount(cidr.prefix)
}
</script>

<template>
  <div class="table-wrap">
    <table class="subnet-table">
      <caption class="visually-hidden">
        {{
          t('table.caption')
        }}
      </caption>
      <thead>
        <tr>
          <th scope="col">{{ t('table.subnet') }}</th>
          <th scope="col">{{ t('table.mask') }}</th>
          <th scope="col">{{ t('table.addressRange') }}</th>
          <th scope="col">{{ t('table.usableRange') }}</th>
          <th scope="col" class="numeric">{{ t('table.hosts') }}</th>
          <th scope="col">{{ t('table.note') }}</th>
          <th scope="col" class="bracket-head" :colspan="bracket.columns">
            <span class="split-key">{{ t('table.split') }}</span
            >/<span class="join-key">{{ t('table.join') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in rows" :key="row.key" :data-color="row.note.color">
          <th scope="row" class="mono">
            {{ row.network }}
            <span v-if="row.tooSmall" class="warn" :title="t('table.belowProviderMinimum')">⚠</span>
          </th>
          <td class="mono muted">{{ row.mask }}</td>
          <td class="mono">{{ rangeOf(row.cidr) }}</td>
          <td class="mono">{{ row.range }}</td>
          <td class="numeric">
            {{ n(row.usable) }}
            <span class="muted">/ {{ n(totalOf(row.cidr)) }}</span>
          </td>
          <td>
            <input
              class="note-input"
              type="text"
              :value="row.note.label"
              :placeholder="t('table.notePlaceholder')"
              :aria-label="t('table.noteFor', { subnet: row.network })"
              @change="
                emit('annotate', row.cidr, {
                  label: ($event.target as HTMLInputElement).value,
                })
              "
            />
            <select
              class="color-select"
              :value="row.note.color"
              :aria-label="t('table.colorFor', { subnet: row.network })"
              @change="
                emit('annotate', row.cidr, {
                  color: ($event.target as HTMLSelectElement).value,
                })
              "
            >
              <option v-for="color in ROW_COLORS" :key="color" :value="color">
                {{ t(`colors.${color}`) }}
              </option>
            </select>
          </td>
          <td class="bracket bracket--split" :colspan="bracket.rows[index]?.splitColSpan ?? 1">
            <button
              type="button"
              :disabled="!row.canSplit"
              :aria-label="t('table.splitSubnet', { subnet: row.network })"
              :title="t('table.splitSubnet', { subnet: row.network })"
              @click="emit('split', row.cidr)"
            >
              <span class="bracket__label">/{{ row.cidr.prefix }}</span>
            </button>
          </td>
          <td
            v-for="bar in bracket.rows[index]?.joins ?? []"
            :key="formatCidr(bar.cidr)"
            class="bracket bracket--join"
            :rowspan="bar.rowSpan"
          >
            <button
              type="button"
              :aria-label="t('table.joinSubnet', { subnet: formatCidr(bar.cidr) })"
              :title="t('table.joinSubnet', { subnet: formatCidr(bar.cidr) })"
              @click="emit('join', bar.cidr)"
            >
              <span class="bracket__label">/{{ bar.cidr.prefix }}</span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
/* Wide tables scroll inside their own box rather than the page. */
.table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.subnet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

th,
td {
  padding: var(--space-2) var(--space-3);
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--color-surface-sunken);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-muted);
}

tbody tr:last-child th,
tbody tr:last-child td {
  border-bottom: none;
}

tbody tr:hover {
  background: var(--color-accent-subtle);
}

.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.muted {
  color: var(--color-text-muted);
}

.warn {
  color: var(--color-warning);
}

/*
 * Row tints are translucent overlays, not opaque fills, so the user's colour choice
 * stays legible against either theme's surface (see AGENTS.md §8).
 */
tbody tr[data-color='blue'] {
  background: color-mix(in srgb, #3b82f6 calc(var(--row-tint-opacity) * 100%), transparent);
}
tbody tr[data-color='green'] {
  background: color-mix(in srgb, #22c55e calc(var(--row-tint-opacity) * 100%), transparent);
}
tbody tr[data-color='amber'] {
  background: color-mix(in srgb, #f59e0b calc(var(--row-tint-opacity) * 100%), transparent);
}
tbody tr[data-color='red'] {
  background: color-mix(in srgb, #ef4444 calc(var(--row-tint-opacity) * 100%), transparent);
}
tbody tr[data-color='purple'] {
  background: color-mix(in srgb, #a855f7 calc(var(--row-tint-opacity) * 100%), transparent);
}
tbody tr[data-color='teal'] {
  background: color-mix(in srgb, #14b8a6 calc(var(--row-tint-opacity) * 100%), transparent);
}

.note-input {
  width: 12rem;
  padding: var(--space-1) var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text);
  font: inherit;
}

.note-input:hover,
.note-input:focus {
  border-color: var(--color-border);
  background: var(--color-surface);
}

.color-select {
  margin-left: var(--space-2);
  padding: var(--space-1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
}

/*
 * The split/join bracket.
 *
 * Each cell is a full-bleed button so the whole coloured bar is the hit target,
 * and `rowspan` on the join cells is what produces the nested-bracket look — the
 * bar's height is the block's share of the table, for free.
 */
.bracket {
  /* The colour lives on the cell, not the button: a cell knows its rowspan height,
     while `height: 100%` on a button inside a <td> has nothing to resolve against. */
  position: relative;
  padding: 0;
  width: 1%;
  min-width: 2.1rem;
  border-bottom: 1px solid var(--color-surface);
  border-left: 1px solid var(--color-surface);
  transition: background var(--transition-fast);
}

.bracket--split {
  background: var(--color-split-bg);
  color: var(--color-split-fg);
}

.bracket--join {
  background: var(--color-join-bg);
  color: var(--color-join-fg);
}

.bracket--split:hover:not(:has(button:disabled)) {
  background: var(--color-split-bg-hover);
}

.bracket--join:hover {
  background: var(--color-join-bg-hover);
}

/* Absolute fill makes the whole coloured bar the hit target, at any rowspan. */
.bracket button {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: var(--text-sm);
  line-height: 1;
  cursor: pointer;
}

/* Vertical labels keep the columns narrow however deep the hierarchy goes. */
.bracket__label {
  writing-mode: vertical-rl;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.bracket button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.bracket button:focus-visible {
  outline: 2px solid var(--color-text);
  outline-offset: -3px;
}

.bracket-head {
  text-align: center;
}

.split-key {
  color: var(--color-split-ink);
  font-weight: 600;
}

.join-key {
  color: var(--color-join-ink);
  font-weight: 600;
}
</style>
